// The CORS proxy an app may be built with (`swiftbrowser build --cors-proxy
// <url>`, packages/cors-proxy). A page reads a cross-origin response only
// when the server allows the page's origin; many servers don't, for images
// especially, which the app reads with `fetch` (natively they load). With a
// proxy, a cross-origin GET or HEAD that fails is tried once more through
// `<proxy>?url=<target>`. Requests go direct first, so a server that allows
// the page (an API, with the user's token) never sees the proxy, and a
// request that may change something (POST, PUT…) is never sent twice.

/** Whether `url` (absolute) is http(s) on another origin than `pageOrigin`. */
import type { FetchLike } from './hostRequests';

export function isCrossOrigin(url: string, pageOrigin: string): boolean {
  try {
    const target = new URL(url);
    return (target.protocol === 'http:' || target.protocol === 'https:') && target.origin !== pageOrigin;
  } catch {
    return false;
  }
}

/** `<proxy>?url=<target>`, keeping a query the proxy URL already has. */
export function proxiedURL(proxy: string, target: string): string {
  const url = new URL(proxy);
  url.searchParams.set('url', target);
  return url.href;
}

/**
 * `fetchImpl`, retrying a failed cross-origin GET or HEAD through `proxy`.
 * A failure is a rejection (CORS refusals and network errors look the same
 * to a page); an answer with any status is the server's and is kept. An
 * aborted request is not retried.
 */
export function withCORSProxy(fetchImpl: FetchLike, proxy: string, pageOrigin: string): FetchLike {
  return async (input, init) => {
    try {
      return await fetchImpl(input, init);
    } catch (error) {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = (init?.method ?? (typeof input === 'object' && 'method' in input ? input.method : 'GET')).toUpperCase();
      const aborted = init?.signal?.aborted || (error instanceof DOMException && error.name === 'AbortError');
      if (aborted || (method !== 'GET' && method !== 'HEAD') || !isCrossOrigin(url, pageOrigin)) throw error;
      return fetchImpl(proxiedURL(proxy, url), init);
    }
  };
}
